import type { FlowVoiceConfig } from './types';

export const flowVoiceProviderIds = [
  'telnyx',
  'aws',
  'azure',
  'elevenlabs',
  'minimax',
  'rime',
  'resemble',
  'inworld',
  'legacy',
  'custom',
] as const;

export type FlowVoiceProviderId = (typeof flowVoiceProviderIds)[number];

const flowVoiceProviderLabels: Record<FlowVoiceProviderId, string> = {
  telnyx: 'Telnyx',
  aws: 'Amazon Polly',
  azure: 'Azure',
  elevenlabs: 'ElevenLabs',
  minimax: 'MiniMax',
  rime: 'Rime',
  resemble: 'Resemble',
  inworld: 'Inworld',
  legacy: 'Legacy',
  custom: 'Custom',
};

export type FlowVoiceCatalogVoice = {
  key: string;
  provider: FlowVoiceProviderId;
  providerLabel: string;
  voiceIdentifier: string;
  voiceId: string;
  voiceName: string;
  modelId?: string;
  modelLabel?: string;
  language?: string;
  locale?: string;
  gender?: string;
  previewUrl?: string;
};

type ParsedFlowVoiceIdentifier = {
  provider: FlowVoiceProviderId;
  voiceId: string;
  modelId?: string;
};

const legacyVoiceIds = new Set(['alice', 'man', 'woman']);

export function getFlowVoiceProviderLabel(provider: FlowVoiceProviderId): string {
  return flowVoiceProviderLabels[provider];
}

export function buildFlowVoiceCatalogKey(provider: FlowVoiceProviderId, voiceId: string, modelId?: string): string {
  return [provider, modelId ?? '', voiceId].join(':');
}

export function buildFlowVoiceCatalogVoice(input: {
  provider: FlowVoiceProviderId;
  voiceIdentifier: string;
  voiceId: string;
  voiceName: string;
  modelId?: string;
  modelLabel?: string;
  language?: string;
  locale?: string;
  gender?: string;
  previewUrl?: string;
}): FlowVoiceCatalogVoice {
  return {
    key: buildFlowVoiceCatalogKey(input.provider, input.voiceId, input.modelId),
    provider: input.provider,
    providerLabel: getFlowVoiceProviderLabel(input.provider),
    voiceIdentifier: input.voiceIdentifier,
    voiceId: input.voiceId,
    voiceName: input.voiceName,
    modelId: input.modelId,
    modelLabel: input.modelLabel ?? input.modelId,
    language: input.language,
    locale: input.locale,
    gender: input.gender,
    previewUrl: input.previewUrl,
  };
}

export function normalizeFlowVoiceProviderId(value: string | undefined): FlowVoiceProviderId | undefined {
  if (!value) return undefined;

  const normalized = value.toLowerCase();
  if (normalized === 'telnyx') return 'telnyx';
  if (normalized === 'aws' || normalized === 'polly') return 'aws';
  if (normalized === 'azure') return 'azure';
  if (normalized === 'elevenlabs') return 'elevenlabs';
  if (normalized === 'minimax') return 'minimax';
  if (normalized === 'rime') return 'rime';
  if (normalized === 'resemble' || normalized === 'resembleai') return 'resemble';
  if (normalized === 'inworld') return 'inworld';
  if (normalized === 'legacy') return 'legacy';
  if (normalized === 'custom') return 'custom';
  return undefined;
}

export function parseFlowVoiceIdentifier(identifier: string): ParsedFlowVoiceIdentifier {
  if (legacyVoiceIds.has(identifier)) {
    return { provider: 'legacy', voiceId: identifier };
  }

  const parts = identifier.split('.');
  const head = normalizeFlowVoiceProviderId(parts[0]);

  if (!head) {
    return { provider: 'custom', voiceId: identifier };
  }

  if (head === 'aws') {
    if (parts[0]?.toLowerCase() === 'polly') {
      return {
        provider: 'aws',
        modelId: 'Polly',
        voiceId: parts.slice(1).join('.'),
      };
    }

    return {
      provider: 'aws',
      modelId: parts.slice(1, -1).join('.') || undefined,
      voiceId: parts.at(-1) ?? identifier,
    };
  }

  if (head === 'azure') {
    return {
      provider: 'azure',
      voiceId: parts.slice(1).join('.') || identifier,
    };
  }

  if (head === 'elevenlabs') {
    return {
      provider: 'elevenlabs',
      modelId: parts.slice(1, -1).join('.') || undefined,
      voiceId: parts.at(-1) ?? identifier,
    };
  }

  if (head === 'telnyx' || head === 'minimax' || head === 'rime' || head === 'resemble' || head === 'inworld') {
    return {
      provider: head,
      modelId: parts.slice(1, -1).join('.') || undefined,
      voiceId: parts.at(-1) ?? identifier,
    };
  }

  return { provider: 'custom', voiceId: identifier };
}

export function flowVoiceCatalogVoiceFromConfig(config: FlowVoiceConfig): FlowVoiceCatalogVoice {
  if (config.voiceType === 'premium') {
    const voiceIdentifier = config.modelId
      ? `ElevenLabs.${config.modelId}.${config.voiceId}`
      : `ElevenLabs.${config.voiceId}`;

    return buildFlowVoiceCatalogVoice({
      provider: 'elevenlabs',
      voiceIdentifier,
      voiceId: config.voiceId,
      voiceName: config.voiceName,
      modelId: config.modelId,
    });
  }

  const parsed = parseFlowVoiceIdentifier(config.voiceId);
  return buildFlowVoiceCatalogVoice({
    provider: parsed.provider,
    voiceIdentifier: config.voiceId,
    voiceId: parsed.voiceId,
    voiceName: config.voiceName,
    modelId: parsed.modelId,
    language: config.language,
  });
}

export function flowVoiceConfigFromCatalogVoice(
  voice: FlowVoiceCatalogVoice,
  currentConfig: FlowVoiceConfig | undefined,
): FlowVoiceConfig {
  if (voice.provider === 'elevenlabs') {
    return {
      voiceType: 'premium',
      voiceId: voice.voiceId,
      voiceName: voice.voiceName,
      modelId: voice.modelId ?? currentConfig?.modelId,
      language: undefined,
      apiKeyRef: undefined,
    };
  }

  return {
    voiceType: 'basic',
    voiceId: voice.voiceIdentifier,
    voiceName: voice.voiceName,
    language: voice.voiceId === 'alice' ? (currentConfig?.language ?? 'en-US') : undefined,
    modelId: undefined,
    apiKeyRef: undefined,
  };
}
